import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, Utensils, Dumbbell, Gift, Bot, ShieldCheck, AlertTriangle, Sparkles } from 'lucide-react';
import { MetabolicType } from '../lib/constants';
interface SolutionPageProps {
  resultType: MetabolicType;
  onContinue: () => void;
}
export function SolutionPage({
  resultType,
  onContinue
}: SolutionPageProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <motion.div initial={{
    opacity: 0,
    x: 50
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -50
  }} className="flex flex-col px-5 pt-6 pb-8 min-h-[100dvh] bg-white max-w-md mx-auto shadow-xl overflow-y-auto hide-scrollbar" data-id="element-307">
      {/* Hope banner */}
      <div className="w-full bg-green-600 text-white py-2 px-3 rounded-xl mb-6 flex items-center justify-center gap-2" data-id="element-308">
        <Sparkles className="w-4 h-4" data-id="element-309" />
        <span className="text-xs font-black uppercase tracking-wide" data-id="element-310">
          La solución existe. Y es para ti.
        </span>
      </div>

      <h1 className="text-2xl font-black text-dark mb-3 leading-tight" data-id="element-311">
        Imagina despertar mañana y por primera vez en AÑOS sentir que tu cuerpo
        trabaja <span className="text-primary" data-id="element-312">CONTIGO</span>.
      </h1>

      <p className="text-gray-600 font-medium mb-6 leading-relaxed" data-id="element-313">
        Especialistas en salud y nutrición de todo el mundo crearon un sistema
        que repara tu metabolismo{' '}
        <span className="uppercase font-bold text-primary" data-id="element-314">{resultType}</span>{' '}
        desde adentro. Tras <strong data-id="element-315">3 años de investigación</strong> y{' '}
        <strong data-id="element-316">10,000+ personas transformadas</strong>.
      </p>

      {/* Why it works */}
      <div className="bg-green-50 rounded-2xl p-5 mb-6 border-2 border-green-200" data-id="element-317">
        <h2 className="text-lg font-black text-dark mb-4 uppercase" data-id="element-318">
          ¿Por qué ESTE plan SÍ funciona?
        </h2>
        <ul className="space-y-3" data-id="element-319">
          <li className="flex items-start gap-3" data-id="element-320">
            <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" data-id="element-321" />
            <span className="text-sm text-gray-800 font-bold" data-id="element-322">
              Diseñado ESPECÍFICAMENTE para tu metabolismo{' '}
              <span className="uppercase text-primary" data-id="element-323">{resultType}</span>. No
              es genérico.
            </span>
          </li>
          <li className="flex items-start gap-3" data-id="element-324">
            <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" data-id="element-325" />
            <span className="text-sm text-gray-800 font-bold" data-id="element-326">
              Trabaja CON tu biología, no contra ella. Tu cuerpo deja de
              almacenar grasa.
            </span>
          </li>
          <li className="flex items-start gap-3" data-id="element-327">
            <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" data-id="element-328" />
            <span className="text-sm text-gray-800 font-bold" data-id="element-329">
              Repara el daño metabólico de AÑOS de dietas genéricas que te
              hicieron más gordo.
            </span>
          </li>
        </ul>
      </div>

      {/* What's included */}
      <h2 className="text-xl font-black text-dark mb-4 uppercase" data-id="element-330">
        ¿Qué incluye tu plan?
      </h2>

      <div className="space-y-3 mb-6" data-id="element-331">
        <div className="bg-white rounded-xl border-2 border-gray-200 p-4 shadow-sm flex gap-3" data-id="element-332">
          <div className="bg-green-100 rounded-full w-11 h-11 flex items-center justify-center shrink-0" data-id="element-333">
            <Utensils className="w-5 h-5 text-green-700" data-id="element-334" />
          </div>
          <div data-id="element-335">
            <h3 className="font-black text-dark text-sm mb-0.5" data-id="element-336">
              Plan de alimentación personalizado
            </h3>
            <p className="text-xs text-gray-600 leading-relaxed" data-id="element-337">
              Para tu metabolismo{' '}
              <span className="uppercase font-bold text-primary" data-id="element-338">
                {resultType}
              </span>
              . Probado y especializado.
            </p>
            <p className="text-xs text-green-700 font-bold mt-1" data-id="element-339">
              → No más contar calorías como loca. No más hambre.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl border-2 border-gray-200 p-4 shadow-sm flex gap-3" data-id="element-340">
          <div className="bg-blue-100 rounded-full w-11 h-11 flex items-center justify-center shrink-0" data-id="element-341">
            <Dumbbell className="w-5 h-5 text-blue-700" data-id="element-342" />
          </div>
          <div data-id="element-343">
            <h3 className="font-black text-dark text-sm mb-0.5" data-id="element-344">
              Plan de ejercicios en casa
            </h3>
            <p className="text-xs text-gray-600 leading-relaxed" data-id="element-345">
              Solo con tu cuerpo. Adaptable al gym si prefieres.
            </p>
            <p className="text-xs text-blue-700 font-bold mt-1" data-id="element-346">
              → No necesitas gym. No necesitas equipo. Solo 20 minutos.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl border-2 border-yellow-200 p-4 shadow-sm flex gap-3" data-id="element-347">
          <div className="bg-yellow-100 rounded-full w-11 h-11 flex items-center justify-center shrink-0" data-id="element-348">
            <Gift className="w-5 h-5 text-yellow-700" data-id="element-349" />
          </div>
          <div data-id="element-350">
            <h3 className="font-black text-dark text-sm mb-0.5" data-id="element-351">
              3 BONOS GRATIS
            </h3>
            <ul className="text-xs text-gray-700 space-y-1 mt-1" data-id="element-352">
              <li className="flex items-start gap-1.5" data-id="element-353">
                <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0 mt-0.5" data-id="element-354" />
                <span data-id="element-355">
                  <strong data-id="element-356">Hidratación inteligente</strong> — aprende a
                  hidratarte correctamente
                </span>
              </li>
              <li className="flex items-start gap-1.5" data-id="element-357">
                <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0 mt-0.5" data-id="element-358" />
                <span data-id="element-359">
                  <strong data-id="element-360">Cómo medir tus macros</strong> — sin complicaciones
                </span>
              </li>
              <li className="flex items-start gap-1.5" data-id="element-361">
                <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0 mt-0.5" data-id="element-362" />
                <span data-id="element-363">
                  <strong data-id="element-364">Lista de compras</strong> — adaptada a tu ciudad y tu
                  dieta
                </span>
              </li>
            </ul>
            <p className="text-xs text-yellow-700 font-bold mt-2" data-id="element-365">
              → Todo lo que necesitas en UN solo lugar.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl border-2 border-purple-200 p-4 shadow-sm flex gap-3" data-id="element-366">
          <div className="bg-purple-100 rounded-full w-11 h-11 flex items-center justify-center shrink-0" data-id="element-367">
            <Bot className="w-5 h-5 text-purple-700" data-id="element-368" />
          </div>
          <div data-id="element-369">
            <h3 className="font-black text-dark text-sm mb-0.5" data-id="element-370">
              Entrenador inteligente virtual
            </h3>
            <p className="text-xs text-gray-600 leading-relaxed" data-id="element-371">
              Acceso indefinido. Ajusta tu plan de por vida.
            </p>
            <p className="text-xs text-purple-700 font-bold mt-1" data-id="element-372">
              → Como tener un nutricionista 24/7 por una FRACCIÓN del costo.
            </p>
          </div>
        </div>
      </div>

      {/* Guarantee */}
      <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 mb-6 flex items-start gap-3" data-id="element-373">
        <ShieldCheck className="w-7 h-7 text-green-600 shrink-0" data-id="element-374" />
        <div data-id="element-375">
          <h3 className="font-black text-dark text-sm mb-1" data-id="element-376">GARANTÍA TOTAL</h3>
          <p className="text-xs text-gray-700 leading-relaxed" data-id="element-377">
            Si no te funciona,{' '}
            <strong data-id="element-378">te devolvemos tu dinero sin preguntar</strong>. Sin letras
            pequeñas. Sin excusas. Riesgo CERO para ti.
          </p>
        </div>
      </div>

      {/* Urgency */}
      <div className="bg-red-50 border-2 border-red-200 rounded-xl p-3 mb-6 flex items-center justify-center gap-2" data-id="element-379">
        <AlertTriangle className="w-4 h-4 text-red-600" data-id="element-380" />
        <p className="text-xs text-red-700 font-black uppercase" data-id="element-381">
          ⚠️ Esta oferta puede NO estar disponible mañana
        </p>
      </div>

      <motion.button whileHover={{
      scale: 1.02
    }} whileTap={{
      scale: 0.98
    }} onClick={onContinue} className="w-full bg-green-600 text-white text-lg font-black py-4 rounded-xl shadow-[0_6px_0_rgb(22,163,74)] hover:bg-green-500 hover:shadow-[0_4px_0_rgb(22,163,74)] hover:translate-y-[2px] active:shadow-[0_0px_0_rgb(22,163,74)] active:translate-y-[6px] transition-all flex items-center justify-center gap-2 uppercase tracking-tight shrink-0" data-id="element-382">
        VER EL PRECIO ESPECIAL
        <ArrowRight className="w-6 h-6" data-id="element-383" />
      </motion.button>
    </motion.div>;
}